import { API_BASE_URL } from "../../config/env";

const BASE_URL = API_BASE_URL;

export default BASE_URL;

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(
    message: string,
    status = 500,
    errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

export type ApiRequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  token?: string | null;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export const getStoredToken = (): string | null => {
  try {
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("token") ||
      null
    );
  } catch {
    return null;
  }
};

const buildUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path;
  return `${BASE_URL}/${path.replace(/^\/+/, "")}`;
};

const readErrorMessage = (payload: unknown, fallback: string) => {
  if (!payload || typeof payload !== "object") return fallback;

  const record = payload as Record<string, unknown>;
  const message = record.message ?? record.error ?? record.detail;

  if (typeof message === "string" && message.trim()) return message;

  return fallback;
};

export const apiRequest = async <T = unknown>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const {
    method = "GET",
    token = getStoredToken(),
    body,
    signal,
    headers: extraHeaders,
  } = options;

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...extraHeaders,
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  if (body !== undefined && !isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(path), {
      method,
      headers,
      signal,
      body:
        body === undefined
          ? undefined
          : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
    });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    throw new ApiError(
      "Unable to reach the server. Check your connection and try again.",
      0
    );
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const errors = (payload as { errors?: Record<string, string[]> })?.errors;
    throw new ApiError(
      readErrorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
      errors
    );
  }

  return payload as T;
};

export interface PaginationMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
  from?: number;
  to?: number;
}

export interface ListResult<T> {
  items: T[];
  meta: PaginationMeta;
}

const DEFAULT_META: PaginationMeta = {
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
};

const toNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseMeta = (
  source: Record<string, unknown> | null | undefined,
  itemCount: number,
  metaSource?: unknown
): PaginationMeta => {
  const meta =
    metaSource && typeof metaSource === "object"
      ? (metaSource as Record<string, unknown>)
      : source ?? {};

  const currentPage = toNumber(meta.current_page ?? meta.currentPage, 1);
  const perPage = toNumber(
    meta.per_page ?? meta.perPage ?? meta.page_size,
    itemCount || DEFAULT_META.perPage
  );
  const total = toNumber(meta.total ?? meta.count, itemCount);
  const lastPage = toNumber(
    meta.last_page ?? meta.lastPage,
    Math.max(1, Math.ceil(total / Math.max(perPage, 1)))
  );

  return {
    currentPage,
    lastPage,
    perPage: perPage || DEFAULT_META.perPage,
    total: total || itemCount,
    from: typeof meta.from === "number" ? meta.from : undefined,
    to: typeof meta.to === "number" ? meta.to : undefined,
  };
};

const looksLikeItemArray = (value: unknown): value is unknown[] =>
  Array.isArray(value);

export const toListResult = <T>(
  payload: unknown,
  fallbackKeys: string[] = []
): ListResult<T> => {
  if (Array.isArray(payload)) {
    return {
      items: payload as T[],
      meta: { ...DEFAULT_META, total: payload.length, perPage: payload.length || DEFAULT_META.perPage },
    };
  }

  if (!payload || typeof payload !== "object") {
    return { items: [], meta: { ...DEFAULT_META } };
  }

  const record = payload as Record<string, unknown>;

  if (looksLikeItemArray(record.data)) {
    return {
      items: record.data as T[],
      meta: parseMeta(record, record.data.length, record.meta),
    };
  }

  if (record.data && typeof record.data === "object") {
    const inner = record.data as Record<string, unknown>;

    if (looksLikeItemArray(inner.data)) {
      return {
        items: inner.data as T[],
        meta: parseMeta(inner, inner.data.length, inner.meta ?? record.meta),
      };
    }

    const nestedKey = fallbackKeys.find((key) => looksLikeItemArray(inner[key]));
    if (nestedKey) {
      const items = inner[nestedKey] as T[];
      return {
        items,
        meta: parseMeta(inner, items.length, inner.meta ?? record.meta),
      };
    }
  }

  const directKey = fallbackKeys.find((key) => looksLikeItemArray(record[key]));
  if (directKey) {
    const items = record[directKey] as T[];
    return {
      items,
      meta: parseMeta(record, items.length, record.meta),
    };
  }

  const looseArrayKey = Object.keys(record).find((key) =>
    looksLikeItemArray(record[key])
  );
  if (looseArrayKey) {
    const items = record[looseArrayKey] as T[];
    return {
      items,
      meta: parseMeta(record, items.length, record.meta),
    };
  }

  return { items: [], meta: { ...DEFAULT_META } };
};

export const unwrapList = <T>(
  payload: unknown,
  fallbackKeys: string[] = []
): T[] => toListResult<T>(payload, fallbackKeys).items;

export const unwrapItem = <T>(payload: unknown): T | null => {
  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;

  if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return record.data as T;
  }

  return record as T;
};

export const buildQuery = (query: Record<string, unknown> = {}) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
};
